import { COLORS } from '../utils/constants';
import convertToOption, {
  convertToEnumWithOptions,
} from '../utils/convert-to-option';
import { getYesNoValue } from '../utils/create-csv';
import getMerchantInfo from './utils/get-merchant-info';

const PITCHED = 'Pitched';
const IN_TALKS = 'In Talks';
const SENT_PROPOSAL = 'Sent Proposal';
const COMMIT_ON_BUDGET = 'Commit on Budget';
const CLOSED_WON = 'Closed Won';
const AGREED = 'Agreed';
const CLOSED_LOST = 'Closed Lost';

export const OPPORTUNITY_STAGES = {
  PITCHED,
  IN_TALKS,
  SENT_PROPOSAL,
  COMMIT_ON_BUDGET,
  CLOSED_WON,
  AGREED,
  CLOSED_LOST,
};

export const stages = Object.values(OPPORTUNITY_STAGES).map(convertToOption);
stages[5].agreed = true;
stages[6].lost = true;

export const stagesWithoutClosedWon = stages.filter(
  option => option.label !== CLOSED_WON,
);

export const allStagesEnum = convertToEnumWithOptions(OPPORTUNITY_STAGES);

export const publicStages = [
  allStagesEnum.PITCHED,
  allStagesEnum.IN_TALKS,
  allStagesEnum.SENT_PROPOSAL,
  allStagesEnum.COMMIT_ON_BUDGET,
  allStagesEnum.AGREED,
  allStagesEnum.CLOSED_LOST,
];

export const STAGE_TO_COLOR_MAP = {
  [PITCHED]: COLORS.WARNING,
  [IN_TALKS]: COLORS.WARNING,
  [SENT_PROPOSAL]: COLORS.WARNING,
  [COMMIT_ON_BUDGET]: COLORS.WARNING,
  [CLOSED_WON]: COLORS.SUCCESS,
  [AGREED]: COLORS.SUCCESS,
  [CLOSED_LOST]: COLORS.SECONDARY,
};

const SELF_SERVICE_CAMPAIGN_STATUSES = {
  SCHEDULED: 'SCHEDULED',
  RUNNING: 'RUNNING',
  PAUSED: 'PAUSED',
  FINISHED: 'FINISHED',
};

export const selfServiceCampaignStatusesOptions = Object.values(
  SELF_SERVICE_CAMPAIGN_STATUSES,
).map(convertToOption);

export const campaignObjectives = {
  AWARENESS: 'Awareness',
  ENGAGEMENT: 'Engagement',
  PERFORMANCE: 'Performance',
  OTHER: 'Other',
};

export const campaignObjectiveOptions =
  Object.values(campaignObjectives).map(convertToOption);

export const campaignTypes = {
  OTHER: 'Other',
  SEASON_SALE: 'Season Sale',
  END_OF_QUARTER: 'End of Quarter',
  PRODUCT_LAUNCH: 'Product Launch',
  SEASONALITY: 'Seasonality',
  LOYALTY_PROGRAM: 'Loyalty Program',
  ZBD: 'ZBD',
  CTO: 'CTO',
  HPTO: 'HPTO',
};

export const campaignTypeOptions =
  Object.values(campaignTypes).map(convertToOption);

export const useCases = {
  SELECTION_BOOST: 'Selection Boost',
  PURE_GROWTH: 'Pure Growth',
};

export const useCasesOptions = Object.values(useCases).map(convertToOption);

export const businessModelValues = {
  WHOLESALE: 'Wholesale',
  PARTNER_PROGRAM: 'Partner Program',
  HYBRID: 'Hybrid',
  CONNECTED_RETAIL: 'Connected Retail',
  NON_ENDEMIC: 'Non Endemic',
};

export const businessModels =
  Object.values(businessModelValues).map(convertToOption);

export const billingTypes = [
  { label: 'External', value: 'External' },
  { label: 'Internal', value: 'Internal' },
  { label: 'WKZ', value: 'WKZ' },
  { label: 'Test', value: 'Test' },
];

export const invoiceBillingTypes = billingTypes.filter(
  ({ value }) => value !== 'Test',
);

export const invoiceTypeEnums = {
  MANUAL: 'Manual',
  AUTOMATIC_MONTHLY: 'Automatic Monthly',
  AUTOMATIC_FINAL: 'Automatic Final',
};

export const invoiceTypeOptions =
  Object.values(invoiceTypeEnums).map(convertToOption);

export const internalBillingTypes = billingTypes.filter(({ value }) =>
  ['WKZ', 'Internal'].includes(value),
);

const coInvestTypeOptions = [
  'Co-Invest PM Offsite',
  'Co-Invest Markets ZBD',
  'Co-Invest Categories',
  'Co-Invest Markets',
  'Co-Invest MOPs Onsite',
  'Co-Invest Markets Sustainability',
  'Co-Invest Stronger Together Q3 20 WS 5 M',
  'Co-Invest Stronger Together Q3 20 PP+CR 5 M',
];

export const coInvestTypes = [
  { label: ' ', value: '' },
  ...coInvestTypeOptions.map(convertToOption),
];

const escalationStageEnum = [
  'Level 0',
  'Level 1',
  'Level 2',
  'Level 3',
  'Level 4',
];

export const escalationStages = escalationStageEnum.map(convertToOption);

export const salesChannelsEnum = {
  MANAGED: 'Managed',
  SELF_SERVICE: 'Self-Service',
  PARTLY_MANAGED: 'Partly Managed',
  SUBSCRIPTION: 'Subscription',
};

export const salesChannels =
  Object.values(salesChannelsEnum).map(convertToOption);
salesChannels.find(
  element => element.label === salesChannelsEnum.SELF_SERVICE,
).disabled = true;

export const getBillingTypes = salesChannel => {
  if (salesChannel === salesChannelsEnum.MANAGED) {
    return billingTypes.filter(
      ({ value }) => !['Internal', 'WKZ'].includes(value),
    );
  }
  return billingTypes;
};

const salesUnitsEnum = {
  EMERGING_PROPOSITIONS: 'Emerging Propositions',
  SPORTS_AND_PREMIUM: 'Sports & Premium',
  STREETWEAR: 'Streetwear',
  YOUNG_FASHION_AND_KIDS: 'Young Fashion & Kids',
  MODERN: 'Modern',
  /* SPC: 'SPC',
  PROPOSITIONS: 'Propositions',
  PC_CORE: 'PC Core',
  PC_PP_PARTNERS: 'PC PP Partners',
  PC_SELF_SERVICE_CLIENT_SERVICES: 'PC Self-Service Client Services',
  NEW_BUSINESS: 'New Business', */
  OTHER: 'Other',
};

export const salesUnits = Object.values(salesUnitsEnum).map(convertToOption);

export const emergingPropositionSalesTeams = ['Beauty', 'Designer'].map(
  convertToOption,
);

export const sportsAndPremiumSalesTeams = ['Sports', 'Premium'].map(
  convertToOption,
);

export const streetwearSalesTeams = ['Streetwear'].map(convertToOption);

export const youngFashionAndKidsSalesTeams = ['Young Fashion', 'Kids'].map(
  convertToOption,
);

export const modernSalesTeams = ['Modern Heritage', 'Modern Classic'].map(
  convertToOption,
);

export const spcSalesTeams = ['SPC Pool 1', 'SPC Pool 2', 'SPC Pool 3'].map(
  convertToOption,
);

export const propositionSalesTeams = ['Beauty'].map(convertToOption);

export const pcCoreSalesTeams = [
  'High Potential Partners',
  'Emerging Partners Pool 1',
  'Emerging Partners Pool 2',
].map(convertToOption);

export const pcPPPartnersSalesTeams = [
  'Growth Partners Pool 1',
  'Growth Partners Pool 2',
  'Core PP Partners',
].map(convertToOption);

export const salesUnitSalesTeams = {
  [salesUnitsEnum.EMERGING_PROPOSITIONS]: emergingPropositionSalesTeams,
  [salesUnitsEnum.SPORTS_AND_PREMIUM]: sportsAndPremiumSalesTeams,
  [salesUnitsEnum.STREETWEAR]: streetwearSalesTeams,
  [salesUnitsEnum.YOUNG_FASHION_AND_KIDS]: youngFashionAndKidsSalesTeams,
  [salesUnitsEnum.MODERN]: modernSalesTeams,
  [salesUnitsEnum.SPC]: spcSalesTeams,
  [salesUnitsEnum.PROPOSITIONS]: propositionSalesTeams,
  [salesUnitsEnum.PC_CORE]: pcCoreSalesTeams,
  [salesUnitsEnum.PC_PP_PARTNERS]: pcPPPartnersSalesTeams,
  [salesUnitsEnum.PC_SELF_SERVICE_CLIENT_SERVICES]: [],
  [salesUnitsEnum.NEW_BUSINESS]: [],
  [salesUnitsEnum.OTHER]: [],
};

export const poolEnum = {
  MEN: 'Men',
  WOMEN: 'Women',
  SPORT: 'Sport',
  SPECIALTIES: 'Specialties',
  OTHER: 'Other',
  SPC: 'SPC',
};
export const pools = Object.values(poolEnum).map(convertToOption);

export const specialtiesSubPools = [
  'Accessories',
  'Beauty',
  'Contemporary',
  'Kids',
  'Premium',
].map(convertToOption);

export const spcSubPools = ['Men', 'Specialties', 'Sports'].map(
  convertToOption,
);

export const poolSubPools = {
  [poolEnum.MEN]: [],
  [poolEnum.WOMEN]: [],
  [poolEnum.SPORT]: [],
  [poolEnum.SPECIALTIES]: specialtiesSubPools,
  [poolEnum.OTHER]: [],
  [poolEnum.SPC]: spcSubPools,
};

export const negotiatedByOptions = [
  { label: 'ZMS', value: 'ZMS' },
  { label: 'Category Management', value: 'Category Management' },
  { label: 'Merchant Operations', value: 'Merchant Operations' },
];

export const csvColumns = [
  { accessor: 'id', label: 'Id' },
  { accessor: 'number', label: 'Number' },
  { accessor: 'name', label: 'Name' },
  { accessor: 'salesUnit', label: 'Sales Unit' },
  { accessor: 'salesTeam', label: 'Sales Team' },
  { accessor: 'owner.firstName', label: 'Owner First Name' },
  { accessor: 'owner.lastName', label: 'Owner Last Name' },
  { accessor: 'stage', label: 'Stage' },
  { accessor: 'probability', label: 'Probability' },
  { accessor: 'businessModel', label: 'Business Model' },
  { accessor: 'expectedRevenue', label: 'Expected Revenue' },
  { accessor: 'closed', label: 'Closed' },
  { accessor: 'accountSfId', label: 'Account ID' },
  { accessor: 'brands[*].name', label: 'Brand Names' },
  { accessor: 'brands[*].code', label: 'Brand Codes' },
  { accessor: 'campaignObjective', label: 'Objective' },
  { accessor: 'useCase', label: 'Use Case' },
  { accessor: 'campaignStart', label: 'Campaign Start' },
  { accessor: 'campaignEnd', label: 'Campaign End' },
  { accessor: 'negotiatedBy', label: 'Negotiated By' },
  { accessor: 'salesChannel', label: 'Sales Channel' },
  { accessor: 'billingType', label: 'Billing Type' },
  { accessor: 'primaryQuote.id', label: 'Quote Id' },
  { accessor: 'primaryQuote.grossOfferValue', label: 'Gross Offer Value' },
  { accessor: 'campaignId', label: 'Campaign Id' },
  {
    accessor: opp =>
      opp.merchants ? getMerchantInfo(opp.merchants, 'name') : '',
    label: 'Merchant Names',
  },
  {
    accessor: opp =>
      opp.merchants ? getMerchantInfo(opp.merchants, 'merchantId') : '',
    label: 'Merchant ID (UUID)',
  },
  { accessor: 'netClientBudget', label: 'Net Client Budget' },
  { accessor: 'created', label: 'Created' },
  { accessor: 'createdBy', label: 'Created By' },
  { accessor: 'lastModified', label: 'Last Modified' },
  { accessor: 'lastModifiedBy', label: 'Last Modified By' },
  { accessor: 'primaryQuote.flexibilityTerms', label: 'Flexiblity Terms' },
  {
    accessor: 'primaryQuote.allowedFlexibilities',
    label: 'Allowed Flexibilities',
  },
  {
    accessor: opp =>
      opp.campaign ? getYesNoValue(opp.campaign.hasScalableBudget) : '',
    label: 'Scalable Budget',
  },
  {
    accessor: opp =>
      opp.account ? getYesNoValue(opp.account.eligibleForCampaigns) : '',
    label: 'Account eligible for Campaigns',
  },
  {
    accessor: 'selfServiceCampaignStatus',
    label: 'Self Service Campaign Status',
  },
  { accessor: 'selfServiceNCode', label: 'Self Service N-Code' },
  { accessor: 'selfServiceMerchant.name', label: 'Self Service Merchant' },
  {
    accessor: 'selfServiceMerchant.merchantId',
    label: 'Self Service Merchant ID (UUID)',
  },
  { accessor: 'creativePool', label: 'Creative Team' },
  { accessor: 'pc1.firstName', label: 'PC1 First Name' },
  { accessor: 'pc1.lastName', label: 'PC1 Last Name' },
  { accessor: 'campaignType', label: 'Campaign Type' },
  { accessor: 'useCase', label: 'Use Case' },
  { accessor: 'campaignObjective', label: 'Campaign Objective' },
  { accessor: 'commercialPackage.value', label: 'Commercial Package' },
  {
    accessor: 'primaryQuote.commercialIncentive',
    label: 'Commercial Incentive',
  },
];

export const creativePoolEnum = {
  MEN: 'Men',
  SPORTS: 'Sports',
  SPECIALTIES: 'Specialties',
  WOMEN_AND_NIKE: 'Women & Nike',
};

export const creativePoolOptions =
  Object.values(creativePoolEnum).map(convertToOption);

export const brandCodeFieldText =
  'Use this field to add comma separated brand codes in bulk';

export const opportunityFilterFields = ['name', 'id'];

export const opportunitySelectLabel = object => {
  return `${object.id} ${object.name}`;
};
